import { Card, CardContent } from "@/components/ui/card";
import { Target, Zap, Settings } from "lucide-react";

const features = [
  {
    title: "Every Question Format Your Roles Actually Need",
    description: "Evaluate candidates across MCQs, long-form essays, audio responses, asynchronous video interviews, and live coding challenges. All questions generated from your job description, all formats delivered in one candidate experience.",
    icon: Target,
    gradient: "from-purple-500 to-violet-600",
    context: "Single-format tools force candidates through multiple assessment stages, adding 5-7 days to your hiring cycle. Testera evaluates technical skills, communication ability, and role-specific knowledge in one 45-minute session."
  },
  {
    title: "Questions That Match Your Exact Requirements",
    description: "Upload a job description. Our AI extracts required skills, suggests question distribution across formats, and generates assessment content with scoring rubrics. Review and edit before deployment—or use as-is for standard roles.",
    icon: Zap,
    gradient: "from-pink-500 to-red-500",
    context: "Building custom assessments manually takes 8-12 hours per role. Generic test libraries miss your company-specific processes and tools. Testera generates tailored questions in under 5 minutes."
  },
  {
    title: "Deployed by HR Teams, Not Engineering",
    description: "Flexible API integrations. Flexible SSO configuration. No multi-week implementation cycles. Create an account, upload your first job description, and start assessing candidates the same day. Built for HR professionals who need results fast.",
    icon: Settings,
    gradient: "from-orange-500 to-yellow-500",
    context: "Enterprise assessment platforms average 6-8 weeks from contract to first assessment. During that time, your competitors are already screening candidates. Testera eliminates the implementation bottleneck."
  }
];

export const FeatureSpotlightGrid = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-6 lg:px-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            From Job Description to Hire-Ready Assessment in{" "}
            <span className="text-secondary">Three Clicks</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Upload your JD. Review AI-generated questions. Deploy to candidates.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <Card 
              key={index}
              className="flex flex-col h-full group hover:shadow-2xl transition-all duration-300 hover:scale-105 relative overflow-hidden border-t-4 border-t-transparent hover:border-t-secondary"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <CardContent className="flex flex-col flex-1 p-8 relative z-10">
                {/* Icon */}
                <div className={`mx-auto w-20 h-20 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                  <feature.icon className="h-10 w-10 text-white" />
                </div>

                {/* Title - Fixed minimum height for alignment */}
                <h3 className="text-2xl font-bold text-foreground mb-4 min-h-[64px] leading-tight">
                  {feature.title}
                </h3>

                {/* Description - Flexible grow */}
                <p className="text-base text-muted-foreground mb-6 leading-relaxed flex-grow">
                  {feature.description}
                </p>

                {/* Competitive Context Box - MINIMUM HEIGHT for alignment */}
                <div className="bg-muted/50 border-l-4 border-primary p-5 rounded-r-lg min-h-[160px] flex flex-col">
                  <strong className="block text-sm font-bold text-foreground mb-2">
                    Why This Matters:
                  </strong>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-grow">
                    {feature.context}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
